import dayjs from 'dayjs';
import { VesselGlobal } from '../../forms/types/FormDefinition';
import { SubmissionGlobalValue } from '../types/ComputerDefinition';

/**
 * Extract only the ids of a field's formula and return them.
 */
export function extractIds(
  { formula, removePrefix = true }: { formula: string, removePrefix?: boolean },
) {
  if (!formula) {
    return [];
  }

  const matches = formula.match(/[$@£#][\w-:]+/g);
  if (!matches) {
    return [];
  }

  return (removePrefix) ? matches.map((id) => id.slice(1)) : matches;
}

function getMostRecentlyUpdatedGlobal(globals: SubmissionGlobalValue[]): SubmissionGlobalValue {
  let mostRecentlyUpdatedGlobal: SubmissionGlobalValue = globals[0];

  globals.forEach((global) => {
    if (mostRecentlyUpdatedGlobal.updated
      && dayjs(global.updated).isAfter(mostRecentlyUpdatedGlobal.updated)
    ) {
      mostRecentlyUpdatedGlobal = global;
    }
  });

  return mostRecentlyUpdatedGlobal;
}

function filterPreviousGlobals(
  allGlobals: SubmissionGlobalValue[],
  globalId: string,
  reportDate: Date | string,
): SubmissionGlobalValue[] {
  return allGlobals
    .filter((global) => global.globalId === globalId
      && global.value
      && dayjs(global.reportDate).isBefore(dayjs(reportDate)));
}

function sortGlobals(globals: SubmissionGlobalValue[]): SubmissionGlobalValue[] {
  return globals.sort((a, b) => {
    const reportDateA = dayjs(a.reportDate);
    const reportDateB = dayjs(b.reportDate);

    if (reportDateA.isBefore(reportDateB)) {
      return 1;
    }

    if (reportDateB.isBefore(reportDateA)) {
      return -1;
    }

    return 0;
  });
}

function findVesselGlobal(
  vesselGlobals: VesselGlobal[],
  globalId: string,
  reportDate: Date | string,
): SubmissionGlobalValue | null {
  const foundGlobal = vesselGlobals
    .find((vesselGlobal) => vesselGlobal.sharedGlobalId === globalId);

  if (!foundGlobal) {
    // There is no point doing the search if we have no globals to look through
    return null;
  }
  return {
    ...foundGlobal,
    created: dayjs(reportDate).subtract(1, 'day').toISOString(),
    updated: dayjs(reportDate).subtract(1, 'day').toISOString(),
    deleted: false,
    reportDate: dayjs(reportDate).subtract(1, 'day').toISOString(),
    globalId: foundGlobal.sharedGlobalId!,
    submissionId: foundGlobal.id,
    value: foundGlobal.value,
  };
}

/**
 * Get a global field's value based on the date of the current report (the previous known value).
 * This uses a binary search to be more performant at higher volumes of data.
 */
export function getPreviousGlobalValue({
  globalId, globalValues, vesselGlobals = [], reportDate,
}: {
  globalId: string,
  globalValues: SubmissionGlobalValue[],
  vesselGlobals: VesselGlobal[],
  reportDate: Date | string,
}): SubmissionGlobalValue | null {
  /**
   * First find previous globals in submissions globals
   */
  const filteredPreviousGlobals = filterPreviousGlobals(globalValues, globalId, reportDate);

  if (filteredPreviousGlobals.length) {
    /**
     * If we found previous submission globals we can proceed and
     * get the most recent previous value.
     */
    const sortedGlobals = sortGlobals(filteredPreviousGlobals);
    const mostRecentPreviousReportDate = sortedGlobals[0].reportDate;

    /**
     * Check if we have multiple submissions with the reportDate
     * equal to mostRecentPreviousReportDate.
     * If there is only one submission, return that one.
     * If we do have multiple globals we need to get the most recently updated one.
     */
    const mostRecentPreviousGlobals = sortedGlobals
      .filter((value) => dayjs(value.reportDate).isSame(mostRecentPreviousReportDate));

    return mostRecentPreviousGlobals.length === 1
      ? mostRecentPreviousGlobals[0]
      : getMostRecentlyUpdatedGlobal(mostRecentPreviousGlobals);
  }

  /**
   * If we haven't found any previous submission globals we should look to see if we can find a
   * value that is in the vessel globals.
   */
  return findVesselGlobal(vesselGlobals, globalId, reportDate);
}
